import React from 'react';
import { ChevronDown, Filter, X, Star } from 'lucide-react';
import './CapitalFilters.css';

export type Period = 'day' | 'last_5' | 'last_week' | 'last_month' | 'last_3_months' | 'last_6_months' | 'ytd' | 'last_12_months';
export type PriceRange = 'all' | 'under_2000' | '2000_10000' | '10000_50000' | 'over_50000';
export type VariationFilter = 'all' | 'up' | 'down' | 'unchanged';

export type AmountRange = 'all' | 'under_20m' | '20m_50m' | '50m_100m' | 'over_100m';

export interface CapitalFiltersState {
    period: Period;
    priceRange: PriceRange;
    variation: VariationFilter;
    amountRange: AmountRange;
    sector: string;
    favoritesOnly: boolean;
}

interface CapitalFiltersProps {
    filters: CapitalFiltersState;
    onFiltersChange: (filters: CapitalFiltersState) => void;
    sectors: string[];
    totalResults: number;
    filteredResults: number;
}

const PERIOD_OPTIONS = [
    { value: 'day', label: 'Aujourd\'hui' },
    { value: 'last_5', label: '5 derniers jours' },
    { value: 'last_week', label: 'Semaine dernière' },
    { value: 'last_month', label: 'Mois dernier' },
    { value: 'last_3_months', label: '3 derniers mois' },
    { value: 'last_6_months', label: '6 derniers mois' },
    { value: 'ytd', label: '📅 YTD (Année)' },
    { value: 'last_12_months', label: '📈 12 mois' },
];

const PRICE_RANGE_OPTIONS = [
    { value: 'all', label: 'Tous les prix' },
    { value: 'under_2000', label: '< 2 000 FCFA' },
    { value: '2000_10000', label: '2 000 - 10 000 FCFA' },
    { value: '10000_50000', label: '10 000 - 50 000 FCFA' },
    { value: 'over_50000', label: '> 50 000 FCFA' },
];

const AMOUNT_RANGE_OPTIONS = [
    { value: 'all', label: 'Tous les volumes' },
    { value: 'under_20m', label: '< 20M FCFA' },
    { value: '20m_50m', label: '20M - 50M FCFA' },
    { value: '50m_100m', label: '50M - 100M FCFA' },
    { value: 'over_100m', label: '> 100M FCFA' },
];

const VARIATION_OPTIONS = [
    { value: 'all', label: 'Toutes variations' },
    { value: 'up', label: '📈 Hausses uniquement' },
    { value: 'down', label: '📉 Baisses uniquement' },
    { value: 'unchanged', label: '➖ Inchangées' },
];

const CapitalFilters: React.FC<CapitalFiltersProps> = ({
    filters,
    onFiltersChange,
    sectors,
    totalResults,
    filteredResults,
}) => {
    const updateFilter = <K extends keyof CapitalFiltersState>(
        key: K,
        value: CapitalFiltersState[K]
    ) => {
        onFiltersChange({ ...filters, [key]: value });
    };

    const resetFilters = () => {
        onFiltersChange({
            period: 'day',
            priceRange: 'all',
            variation: 'all',
            amountRange: 'all',
            sector: 'all',
            favoritesOnly: false,
        });
    };

    const hasActiveFilters =
        filters.priceRange !== 'all' ||
        filters.amountRange !== 'all' ||
        filters.variation !== 'all' ||
        filters.sector !== 'all' ||
        filters.favoritesOnly;

    return (
        <div className="capital-filters">
            <div className="capital-filters__container">
                {/* Period Filter - Required */}
                <div className="capital-filters__group capital-filters__group--required">
                    <label className="capital-filters__label">
                        Période
                        <span className="capital-filters__required">*</span>
                    </label>
                    <div className="capital-filters__select-wrapper">
                        <select
                            value={filters.period}
                            onChange={(e) => updateFilter('period', e.target.value as Period)}
                            className="capital-filters__select"
                        >
                            {PERIOD_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                        <ChevronDown className="capital-filters__select-icon" size={16} />
                    </div>
                </div>

                {/* Price Range Filter */}
                <div className="capital-filters__group">
                    <label className="capital-filters__label">Prix</label>
                    <div className="capital-filters__select-wrapper">
                        <select
                            value={filters.priceRange}
                            onChange={(e) => updateFilter('priceRange', e.target.value as PriceRange)}
                            className="capital-filters__select"
                        >
                            {PRICE_RANGE_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                        <ChevronDown className="capital-filters__select-icon" size={16} />
                    </div>
                </div>

                {/* Amount (Volume) Range Filter */}
                <div className="capital-filters__group">
                    <label className="capital-filters__label">Volume (M)</label>
                    <div className="capital-filters__select-wrapper">
                        <select
                            value={filters.amountRange}
                            onChange={(e) => updateFilter('amountRange', e.target.value as AmountRange)}
                            className="capital-filters__select"
                        >
                            {AMOUNT_RANGE_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                        <ChevronDown className="capital-filters__select-icon" size={16} />
                    </div>
                </div>

                {/* Variation Filter */}
                <div className="capital-filters__group">
                    <label className="capital-filters__label">Variation</label>
                    <div className="capital-filters__select-wrapper">
                        <select
                            value={filters.variation}
                            onChange={(e) => updateFilter('variation', e.target.value as VariationFilter)}
                            className="capital-filters__select"
                        >
                            {VARIATION_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                        <ChevronDown className="capital-filters__select-icon" size={16} />
                    </div>
                </div>

                {/* Sector Filter */}
                <div className="capital-filters__group">
                    <label className="capital-filters__label">Secteur</label>
                    <div className="capital-filters__select-wrapper">
                        <select
                            value={filters.sector}
                            onChange={(e) => updateFilter('sector', e.target.value)}
                            className="capital-filters__select"
                        >
                            <option value="all">Tous les secteurs</option>
                            {sectors.map((sector) => (
                                <option key={sector} value={sector}>
                                    {sector}
                                </option>
                            ))}
                        </select>
                        <ChevronDown className="capital-filters__select-icon" size={16} />
                    </div>
                </div>

                {/* Favorites Toggle */}
                <div className="capital-filters__group capital-filters__group--toggle">
                    <button
                        className={`capital-filters__toggle ${filters.favoritesOnly ? 'active' : ''}`}
                        onClick={() => updateFilter('favoritesOnly', !filters.favoritesOnly)}
                        title="Mes favoris uniquement"
                    >
                        <Star size={16} fill={filters.favoritesOnly ? '#f59e0b' : 'none'} />
                        <span>Favoris</span>
                    </button>
                </div>

                {/* Reset Button */}
                {hasActiveFilters && (
                    <div className="capital-filters__group capital-filters__group--reset">
                        <button className="capital-filters__reset" onClick={resetFilters}>
                            <X size={14} />
                            <span>Réinitialiser</span>
                        </button>
                    </div>
                )}
            </div>

            {/* Results Counter */}
            <div className="capital-filters__results">
                <Filter size={14} />
                <span>
                    {filteredResults === totalResults
                        ? `${totalResults} actions`
                        : `${filteredResults} sur ${totalResults} actions`}
                </span>
            </div>
        </div>
    );
};

export default CapitalFilters;
